// modules/project-storage.js

// Import de JSZip (doit être disponible globalement ou via CDN)
// Si vous voulez l'importer comme module ES6, décommentez la ligne suivante :
// import JSZip from '../vendor/jszip.min.js';

/**
 * Configuration de localForage pour l'application
 */
const storage = localforage.createInstance({
  name: 'CombicastDB',
  storeName: 'audio_projects',
  description: 'Stockage des projets audio Combicast'
});

/**
 * Convertit un blob en base64
 * @param {Blob} blob - Blob à convertir
 * @returns {Promise<string>} - Données en base64
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convertit un base64 en blob
 * @param {string} base64 - Données en base64
 * @param {string} mimeType - Type MIME du blob
 * @returns {Blob} - Blob créé
 */
function base64ToBlob(base64, mimeType) {
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  
  return new Blob([ab], { type: mimeType });
}

/**
 * Sauvegarde le projet actuel
 * @param {string} projectName - Nom du projet
 * @param {NodeList} audioClips - Liste des clips audio
 * @returns {Promise<Object>} - Résultat de la sauvegarde
 */
export async function saveCurrentProject(projectName, audioClips) {
  try {
    if (!projectName || !projectName.trim()) {
      throw new Error('Le nom du projet est requis');
    }
    
    if (audioClips.length === 0) {
      throw new Error('Aucun clip audio à sauvegarder');
    }
    
    // Créer l'objet projet
    const projectData = {
      name: projectName.trim(),
      savedAt: new Date().toISOString(),
      clips: []
    };
    
    // Traiter chaque clip audio
    for (const clip of audioClips) {
      const label = clip.querySelector('p').textContent;
      const audio = clip.querySelector('audio');
      
      // Récupérer les données audio
      const response = await fetch(audio.src);
      const blob = await response.blob();
      
      // Convertir le blob en base64
      const base64Data = await blobToBase64(blob);
      
      // Ajouter les données du clip
      projectData.clips.push({
        name: label,
        audioData: base64Data,
        duration: audio.duration || 0
      });
    }
    
    // Sauvegarder dans localForage avec un timestamp unique
    const projectId = `project_${Date.now()}`;
    await storage.setItem(projectId, projectData);
    
    return {
      success: true,
      message: 'Projet sauvegardé avec succès!',
      projectId: projectId
    };
    
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du projet:', error);
    return {
      success: false,
      message: error.message || 'Erreur lors de la sauvegarde du projet'
    };
  }
}

/**
 * Récupère tous les projets sauvegardés
 * @returns {Promise<Array>} - Liste des projets
 */
export async function getAllProjects() {
  try {
    const projects = [];
    
    // Récupérer tous les projets de localForage
    await storage.iterate((value, key) => {
      if (key.startsWith('project_')) {
        projects.push({
          id: key,
          ...value
        });
      }
    });
    
    // Trier par date de sauvegarde (le plus récent d'abord)
    projects.sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
    
    return projects;
    
  } catch (error) {
    console.error('Erreur lors du chargement des projets:', error);
    throw error;
  }
}

/**
 * Charge un projet spécifique
 * @param {string} projectId - ID du projet
 * @returns {Promise<Object>} - Données du projet
 */
export async function loadProject(projectId) {
  try {
    const projectData = await storage.getItem(projectId);
    
    if (!projectData) {
      throw new Error('Projet introuvable');
    }
    
    // Convertir les données base64 en blobs pour chaque clip
    const clipsWithBlobs = projectData.clips.map(clip => ({
      name: clip.name,
      blob: base64ToBlob(clip.audioData, 'audio/mp3'),
      duration: clip.duration
    }));
    
    return {
      ...projectData,
      clipsWithBlobs
    };
    
  } catch (error) {
    console.error('Erreur lors du chargement du projet:', error);
    throw error;
  }
}

/**
 * Supprime un projet
 * @param {string} projectId - ID du projet
 * @returns {Promise<boolean>} - Succès de la suppression
 */
export async function deleteProject(projectId) {
  try {
    await storage.removeItem(projectId);
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression du projet:', error);
    throw error;
  }
}

/**
 * Supprime tous les projets
 * @returns {Promise<boolean>} - Succès de la suppression
 */
export async function clearAllProjects() {
  try {
    await storage.clear();
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression de tous les projets:', error);
    throw error;
  }
}

/**
 * Obtient des statistiques sur le stockage
 * @returns {Promise<Object>} - Statistiques
 */
export async function getStorageStats() {
  try {
    const projects = await getAllProjects();
    
    let totalClips = 0;
    let totalSize = 0;
    
    for (const project of projects) {
      totalClips += project.clips.length;
      
      // Estimer la taille
      for (const clip of project.clips) {
        // Taille approximative en octets (base64 est ~33% plus grand que l'original)
        totalSize += clip.audioData.length * 0.75;
      }
    }
    
    return {
      projectCount: projects.length,
      totalClips: totalClips,
      estimatedSize: totalSize,
      estimatedSizeMB: (totalSize / (1024 * 1024)).toFixed(2)
    };
    
  } catch (error) {
    console.error('Erreur lors du calcul des statistiques:', error);
    return {
      projectCount: 0,
      totalClips: 0,
      estimatedSize: 0,
      estimatedSizeMB: '0.00'
    };
  }
}

/**
 * Nettoie un nom de fichier pour le rendre sûr
 * @param {string} name - Nom à nettoyer
 * @returns {string} - Nom nettoyé
 */
function sanitizeFileName(name) {
  return name.replace(/[^a-z0-9_\-àâäéèêëïîôöùûüÿæœç]/gi, '_');
}

/**
 * Exporte un projet en créant un fichier ZIP
 * @param {string} projectId - ID du projet
 * @param {Function} onProgress - Callback de progression (optionnel)
 */
export async function exportProject(projectId, onProgress) {
  try {
    if (typeof JSZip === 'undefined') {
      throw new Error('JSZip n\'est pas chargé');
    }
    
    const projectData = await storage.getItem(projectId);
    if (!projectData) {
      throw new Error('Projet introuvable');
    }
    
    // Créer une instance JSZip
    const zip = new JSZip();
    
    // Nom du projet nettoyé pour le dossier
    const projectFolderName = sanitizeFileName(projectData.name);
    
    // Ajouter chaque clip au ZIP
    for (let i = 0; i < projectData.clips.length; i++) {
      const clip = projectData.clips[i];
      const blob = base64ToBlob(clip.audioData, 'audio/mp3');
      
      // Nom du fichier nettoyé
      const fileName = `${sanitizeFileName(clip.name)}.mp3`;
      
      // Ajouter le fichier dans un dossier avec le nom du projet
      zip.file(`${projectFolderName}/${fileName}`, blob);
      
      // Callback de progression
      if (onProgress) {
        onProgress(i + 1, projectData.clips.length);
      }
    }
    
    // Générer le ZIP
    const zipBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });
    
    // Télécharger le ZIP
    const url = URL.createObjectURL(zipBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${projectFolderName}.zip`;
    anchor.click();
    URL.revokeObjectURL(url);
    
    return {
      success: true,
      count: projectData.clips.length
    };
    
  } catch (error) {
    console.error('Erreur lors de l\'export du projet:', error);
    throw error;
  }
}

/**
 * Exporte tous les projets en créant un fichier ZIP
 * @param {Function} onProgress - Callback de progression (optionnel)
 */
export async function exportAllProjects(onProgress) {
  try {
    if (typeof JSZip === 'undefined') {
      throw new Error('JSZip n\'est pas chargé');
    }
    
    const projects = await getAllProjects();
    
    if (projects.length === 0) {
      throw new Error('Aucun projet à exporter');
    }
    
    // Créer une instance JSZip
    const zip = new JSZip();
    
    let totalFiles = 0;
    let processedFiles = 0;
    
    // Compter le nombre total de fichiers
    for (const project of projects) {
      totalFiles += project.clips.length;
    }
    
    // Ajouter chaque projet dans son propre dossier
    for (const project of projects) {
      const projectFolderName = sanitizeFileName(project.name);
      
      for (const clip of project.clips) {
        const blob = base64ToBlob(clip.audioData, 'audio/mp3');
        const fileName = `${sanitizeFileName(clip.name)}.mp3`;
        
        // Ajouter le fichier dans le dossier du projet
        zip.file(`${projectFolderName}/${fileName}`, blob);
        
        processedFiles++;
        
        // Callback de progression
        if (onProgress) {
          onProgress(processedFiles, totalFiles);
        }
      }
    }
    
    // Générer le ZIP
    const zipBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });
    
    // Télécharger le ZIP
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    const url = URL.createObjectURL(zipBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `combicast_tous_projets_${timestamp}.zip`;
    anchor.click();
    URL.revokeObjectURL(url);
    
    return {
      success: true,
      projectCount: projects.length,
      fileCount: totalFiles
    };
    
  } catch (error) {
    console.error('Erreur lors de l\'export de tous les projets:', error);
    throw error;
  }
}